import React from 'react'
import { useAtom } from 'jotai'
import Classnames from 'classnames'
import Cookies from 'js-cookie'

// helper functions
import { returnValue } from '@/utils/op-types/helperFunctions'

// store
import {
  isBuilder,
  isLoggedIn,
  manifestStatic,
  pageContentStatic,
  manifestDesign,
  pageContentDesign,
  manifestBlocks,
  pageContentBlocks,
  openSignUpModal,
  openShareModal,
} from '@/utils/op-types/store'

import { RiFileCopy2Line, RiSave2Line } from 'react-icons/ri'

// block renderer
import BlockRenderer from '@/components/optypes/frontend/BlockRenderer'

// block builder tools
import StaticContentWrapper from '@/components/optypes/frontend/StaticContentWrapper'
import AddBlockBuilder from '@/components/optypes/frontend/AddBlockBuilder'

// blocks
import Checklist from '@/components/optypes/frontend/SimpleChecklist/blocks/Checklist'
import Content from '@/components/optypes/frontend/SimpleChecklist/blocks/Content'
import Image from '@/components/optypes/frontend/SimpleChecklist/blocks/Image'

import OnepagerFooter from '@/components/optypes/headlesss-components/OnepagerFooter'

const Components = {
  checklist: Checklist,
  content: Content,
  image: Image,
}

const MainComponentRenderer = () => {
  const [builder] = useAtom(isBuilder)
  // manifest settings
  const [opStatic] = useAtom(manifestStatic)
  const [opDesign] = useAtom(manifestDesign)
  const [opBlocks] = useAtom(manifestBlocks)
  // custom page settings
  const [pageStatic] = useAtom(pageContentStatic)
  const [pageDesign] = useAtom(pageContentDesign)
  const [pageBlocks] = useAtom(pageContentBlocks)

  const [getLoggedIn, setLoggedIn] = useAtom(isLoggedIn)

  const [isSignUpModalOpen, toggleSignUpModal] = useAtom(openSignUpModal)
  const [isShareModalOpen, toggleShareModal] = useAtom(openShareModal)

  return (
    <div
      className="min-h-screen pb-12 opr-background"
      style={{
        backgroundColor: returnValue('main_background', opDesign, pageDesign),
        color: returnValue('text_color', opDesign, pageDesign),
      }}
    >
      <div
        className={Classnames(
          `w-full opr-mainheader pb-24 pt-20 bg-gray-800 overflow-hidden relative ${returnValue(
            'header_shadow',
            opDesign,
            pageDesign
          )}`,
          {}
        )}
        style={{
          backgroundColor: returnValue(
            'header_background',
            opDesign,
            pageDesign
          ),
          color: returnValue('header_text_color', opDesign, pageDesign),
        }}
      >
        {/* background image */}
        {returnValue('header_background_image', opDesign, pageDesign) && (
          <div
            className="absolute top-0 left-0 z-0 h-full w-full  bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url(${returnValue(
                'header_background_image',
                opDesign,
                pageDesign
              )})`,
              opacity: returnValue('bgimgopacity', opDesign, pageDesign),
            }}
          ></div>
        )}

        <div
          className="w-full opr-header-text mx-auto pt-6 text-center pb-6 relative"
          style={{ maxWidth: '960px' }}
        >
          <h1 className="font-bold text-6xl mb-2">
            <StaticContentWrapper contentKey="main_headline">
              {returnValue('main_headline', opStatic, pageStatic)}
            </StaticContentWrapper>
          </h1>

          <h5 className="font-medium opacity-65 text-2xl mb-4">
            <StaticContentWrapper contentKey="sub_headline">
              {returnValue('sub_headline', opStatic, pageStatic)}
            </StaticContentWrapper>
          </h5>

          <div className="flex items-center justify-center mt-8 font-medium space-x-3">
            <StaticContentWrapper contentKey="sub_headline">
              <img
                src={returnValue('authorimage', opStatic, pageStatic)}
                alt={returnValue('authorname', opStatic, pageStatic)}
                className="rounded-full w-8"
              />
            </StaticContentWrapper>
            <span>
              <StaticContentWrapper contentKey="sub_headline">
                {returnValue('authorname', opStatic, pageStatic)}
              </StaticContentWrapper>
            </span>
          </div>

          <div className="mt-8 flex justify-center">
            {!getLoggedIn && (
              <button
                onClick={() => {
                  if (!builder) {
                    toggleSignUpModal(true)
                  }
                }}
                className="opr-savebutton bg-black text-xl hover:bg-blue-600 inline-block cursor-pointer hover:shadow-md py-3 px-6 rounded text-white font-bold"
                style={{
                  backgroundColor: returnValue(
                    'button_background',
                    opDesign,
                    pageDesign
                  ),
                  color: returnValue('button_text_color', opDesign, pageDesign),
                }}
              >
                Save Your Changes
              </button>
            )}

            {/* share button */}
            {getLoggedIn && (
              <div>
                <div
                  onClick={() => {
                    toggleShareModal(true)
                  }}
                  className="py-2 px-4 flex items-center border border-black border-opacity-20  rounded text-lg  font-bold text-white cursor-pointer"
                  style={{
                    backgroundColor: returnValue(
                      'button_background',
                      opDesign,
                      pageDesign
                    ),
                    color: returnValue(
                      'button_text_color',
                      opDesign,
                      pageDesign
                    ),
                  }}
                >
                  <div>Share</div>
                  <div className="ml-3">
                    <RiFileCopy2Line />
                  </div>
                </div>
                <div
                  className={Classnames(
                    'rounded  text-center text-xs py-2 px-3 font-medium'
                  )}
                >
                  <div className="text-xs opacity-70">Signed in as</div>
                  <div className="font-bold">{Cookies.get('wl-email')}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="w-full opr-container pt-12 -mt-28 relative">
        {pageBlocks.map((block, index) => (
          <BlockRenderer
            key={index}
            index={index}
            Components={Components}
            block={block}
          />
        ))}
      </div>

      {builder && (
        <div
          className="w-full mx-auto py-6 flex justify-center"
          style={{ maxWidth: '600px' }}
        >
          <AddBlockBuilder btnText="Add Block" />
        </div>
      )}

      <div className="py-12 z-20 relative">
        <OnepagerFooter />
      </div>
    </div>
  )
}

export default MainComponentRenderer
